/**
 * The market registry: every country Discover knows about, live or not.
 *
 * To add a market, add an entry here, a translation file in
 * `src/i18n/locales/`, a flag in `src/components/flags/`, and the city images
 * in `public/search/`. `NEXT_PUBLIC_DISCOVER_MARKETS` decides which markets
 * are live. See `docs/agents/adding-a-market.md`.
 */

import { parseMarketList } from "./env";

export interface LatLng {
  readonly lat: number;
  readonly lng: number;
}

/**
 * How an organizer starts listing an event: `form` is the "List my club" lead
 * form, `guide` is the "List your event" guide.
 */
export type OrganizerMode = "form" | "guide";

interface MarketDefinition {
  /** URL segment and next-intl locale. */
  readonly locale: string;
  /** Bare language tags (`nb`) that resolve to this market. */
  readonly languages: readonly string[];
  readonly defaultArea: string;
  /** Map areas in display order. */
  readonly areas: Readonly<Record<string, LatLng>>;
  /** ISO 4217 code for events that carry no currency. */
  readonly currency: string;
  /** Background behind the city images. A whole class name, so Tailwind finds it. */
  readonly accentClass: string;
  /** Live when `NEXT_PUBLIC_DISCOVER_MARKETS` is unset. */
  readonly launched: boolean;
  readonly organizerMode: OrganizerMode;
}

export const markets = {
  GB: {
    locale: "en-gb",
    languages: ["en"],
    defaultArea: "london",
    areas: {
      london: { lat: 51.5074, lng: -0.1278 },
      manchester: { lat: 53.4808, lng: -2.2426 },
      birmingham: { lat: 52.4862, lng: -1.8904 },
      bristol: { lat: 51.4545, lng: -2.5879 },
      leeds: { lat: 53.8008, lng: -1.5491 },
    },
    currency: "GBP",
    accentClass: "bg-orange-50",
    launched: true,
    organizerMode: "form",
  },
  NO: {
    locale: "nb-no",
    languages: ["nb", "no"],
    defaultArea: "oslo",
    areas: {
      oslo: { lat: 59.9139, lng: 10.7522 },
      bergen: { lat: 60.3913, lng: 5.3221 },
      trondheim: { lat: 63.4305, lng: 10.3951 },
      stavanger: { lat: 58.97, lng: 5.7331 },
      tromso: { lat: 69.6496, lng: 18.956 },
    },
    currency: "NOK",
    accentClass: "bg-blue-50",
    launched: true,
    organizerMode: "form",
  },
  DE: {
    locale: "de-de",
    languages: ["de"],
    defaultArea: "munich",
    areas: {
      munich: { lat: 48.1351, lng: 11.582 },
    },
    currency: "EUR",
    accentClass: "bg-amber-50",
    launched: false,
    organizerMode: "guide",
  },
  FR: {
    locale: "fr-fr",
    languages: ["fr"],
    defaultArea: "marseille",
    areas: {
      marseille: { lat: 43.2965, lng: 5.3698 },
    },
    currency: "EUR",
    accentClass: "bg-sky-50",
    launched: false,
    organizerMode: "guide",
  },
} as const satisfies Record<string, MarketDefinition>;

type Markets = typeof markets;

export type CountryCode = keyof Markets;
type Market = Markets[CountryCode];
export type AppLocale = Market["locale"];
export type MapAreaId = {
  [C in CountryCode]: keyof Markets[C]["areas"] & string;
}[CountryCode];

export const defaultCountry: CountryCode = "GB";

export const registeredCountries = Object.keys(markets) as CountryCode[];

export const liveCountries: readonly CountryCode[] =
  parseMarketList(
    process.env.NEXT_PUBLIC_DISCOVER_MARKETS,
    registeredCountries,
  ) ?? registeredCountries.filter((code) => markets[code].launched);

// Not-live locales redirect to the default locale, so it must stay live.
if (!liveCountries.includes(defaultCountry)) {
  throw new Error(
    `NEXT_PUBLIC_DISCOVER_MARKETS must include ${defaultCountry}, the default market.`,
  );
}

export function isLiveCountry(code: CountryCode): boolean {
  return liveCountries.includes(code);
}

/** The registered market whose URL locale is `locale`, live or not. */
export function findCountryByLocale(locale: string): CountryCode | undefined {
  return registeredCountries.find((code) => markets[code].locale === locale);
}
